import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button, EmptyState } from "@nexus/ui";
import {
  HISTORY_FULFILLMENT_STATUSES,
  TERMINAL_FULFILLMENT_STATUSES,
} from "@nexus/contracts";

import { ordersQueryOptions } from "@/lib/queries";
import {
  toOrderLineTypeFilter,
  type OrderLineTypeFilterOption,
} from "@/features/marketplace/lib/order-line-kind";
import type { MarketplaceOrder } from "@/features/marketplace/types/marketplace.types";

import { OrdersList } from "./orders-list/OrdersList";
import { OrdersListSkeleton } from "./orders-list/OrdersListSkeleton";

const ORDERS_TAB_PAGE_SIZE = 50;

export type OrdersTabVariant = "in-production" | "history" | "archive";

interface OrdersTabProps {
  tenantId: string;
  variant: OrdersTabVariant;
  /** Order id to auto-expand, highlight, and scroll into view (deep link). */
  highlightOrderId?: string;
  highlightScriptId?: string;
  /** When embedded in a composite tab, skip the empty-state message. */
  hideEmptyState?: boolean;
}

export function filterOrdersByTabVariant(
  orders: MarketplaceOrder[],
  variant: OrdersTabVariant,
): MarketplaceOrder[] {
  if (variant === "history") {
    return orders.filter(
      (order) =>
        (HISTORY_FULFILLMENT_STATUSES as readonly string[]).includes(
          order.fulfillmentStatus,
        ),
    );
  }
  if (variant === "archive") {
    return orders.filter(
      (order) => order.fulfillmentStatus === "CANCELLED",
    );
  }
  return orders.filter(
    (order) =>
      !(TERMINAL_FULFILLMENT_STATUSES as readonly string[]).includes(
        order.fulfillmentStatus,
      ),
  );
}

export function OrdersTab({
  tenantId,
  variant,
  highlightOrderId,
  highlightScriptId,
  hideEmptyState = false,
}: OrdersTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [loadedOrders, setLoadedOrders] = useState<MarketplaceOrder[]>([]);
  const [lineType, setLineType] = useState<OrderLineTypeFilterOption>("all");
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const fulfillmentGroup =
    variant === "in-production" ? "active" : variant;
  const { data, isLoading, isError, refetch, isFetching, isRefetching } =
    useQuery(
      ordersQueryOptions(tenantId, {
        fulfillmentGroup,
        lineType: toOrderLineTypeFilter(lineType),
        limit: ORDERS_TAB_PAGE_SIZE,
        offset,
      }),
    );

  useEffect(() => {
    setOffset(0);
    setLoadedOrders([]);
    setLineType("all");
    setHasLoadedOnce(false);
  }, [tenantId, variant]);

  const handleLineTypeChange = useCallback(
    (next: OrderLineTypeFilterOption) => {
      setLineType(next);
      setOffset(0);
    },
    [],
  );

  useEffect(() => {
    if (!data) return;
    setHasLoadedOnce(true);
    setLoadedOrders((current) => {
      if (offset === 0) return data.orders;
      const seen = new Set(current.map((order) => order.id));
      const next = [...current];
      for (const order of data.orders) {
        if (!seen.has(order.id)) next.push(order);
      }
      return next;
    });
  }, [data, offset]);

  const orders = loadedOrders;
  const tabOrders = useMemo(() => filterOrdersByTabVariant(orders, variant), [orders, variant]);
  const total = data?.total ?? 0;
  const canLoadMore = offset + ORDERS_TAB_PAGE_SIZE < total;

  // Only the first load for this tenant and tab shows the skeleton; later page or Type loads keep
  // the list mounted, even when empty, so its toolbar filters survive.
  if (isLoading && !hasLoadedOnce) {
    return <OrdersListSkeleton />;
  }

  if (isError) {
    return (
      <EmptyState
        variant="error"
        title={t("marketplace.orders.list.error.title")}
        description={t("marketplace.orders.list.error.description")}
        className="rounded-md border border-dashed border-border"
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart="refresh-cw"
            disabled={isRefetching}
            onClick={() => void refetch()}
          >
            {t("marketplace.orders.list.error.retry")}
          </Button>
        }
      />
    );
  }

  if (tabOrders.length === 0 && lineType === "all") {
    if (hideEmptyState) return null;
    const allDelivered =
      variant === "in-production" &&
      orders.length > 0 &&
      orders.every((order) =>
        (HISTORY_FULFILLMENT_STATUSES as readonly string[]).includes(
          order.fulfillmentStatus,
        ),
      );
    if (allDelivered) {
      return (
        <EmptyState
          icon="check-circle"
          title={t("marketplace.orders.list.allDelivered.title")}
          description={t("marketplace.orders.list.allDelivered.description")}
          className="rounded-md border border-success-border bg-success-subtle/30"
        />
      );
    }
    return variant === "history" ? (
      <EmptyState
        icon="history"
        title={t("marketplace.orders.list.emptyHistory.title")}
        description={t("marketplace.orders.list.emptyHistory.description")}
        className="rounded-md border border-dashed border-border"
      />
    ) : variant === "archive" ? (
      <EmptyState
        icon="file"
        title={t(
          "marketplace.orders.list.emptyArchive.title",
          "No cancelled orders",
        )}
        description={t(
          "marketplace.orders.list.emptyArchive.description",
          "Orders move here when their scripts are cancelled.",
        )}
        className="rounded-md border border-dashed border-border"
      />
    ) : (
      <EmptyState
        icon="clapperboard"
        title={t("marketplace.orders.list.empty.title")}
        description={t("marketplace.orders.list.empty.description")}
        className="rounded-md border border-dashed border-border"
        action={
          <Button
            type="button"
            size="sm"
            onClick={() => void navigate({ to: "/marketplace", search: { tab: "vendors" } })}
          >
            {t("marketplace.orders.list.empty.action")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <OrdersList
        orders={tabOrders}
        tenantId={tenantId}
        variant={variant}
        highlightOrderId={highlightOrderId}
        highlightScriptId={highlightScriptId}
        lineType={lineType}
        onLineTypeChange={handleLineTypeChange}
      />
      {canLoadMore ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => setOffset((current) => current + ORDERS_TAB_PAGE_SIZE)}
          >
            {t("marketplace.orders.list.loadMore", "Load more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
